import { useMemo, useRef } from 'react';

import { type ToolApprovalDecisionTarget } from '@apps/web/components/phoebe_chat/transcript_types';
import { type V3ChatActions } from '@apps/web/features/phoebe_chat_v3/model/actions';

export function useStableV3ChatActions({
    actions,
    onPromptSubmit,
}: {
    readonly actions: V3ChatActions;
    readonly onPromptSubmit?: (prompt: string) => Promise<boolean> | boolean;
}) {
    const callbacksRef = useRef({
        onToolDecision: actions.onToolDecision,
        onQuestionAnswer: actions.onQuestionAnswer,
        onRetry: actions.onRetry,
        onPromptSubmit,
    });
    callbacksRef.current = {
        onToolDecision: actions.onToolDecision,
        onQuestionAnswer: actions.onQuestionAnswer,
        onRetry: actions.onRetry,
        onPromptSubmit,
    };
    const hasToolDecision = actions.onToolDecision !== undefined;
    const hasQuestionAnswer = actions.onQuestionAnswer !== undefined;
    const hasRetry = actions.onRetry !== undefined;
    const hasPromptSubmit = onPromptSubmit !== undefined;

    return useMemo<V3ChatActions>(
        () => ({
            decidingCallIds: actions.decidingCallIds,
            answeringQuestionCallIds: actions.answeringQuestionCallIds,
            isRetryDisabled: actions.isRetryDisabled,
            ...(hasToolDecision
                ? {
                      onToolDecision: (
                          target: ToolApprovalDecisionTarget,
                          decision: 'deny' | 'allow_once',
                      ) =>
                          callbacksRef.current.onToolDecision?.(
                              target,
                              decision,
                          ),
                  }
                : {}),
            ...(hasQuestionAnswer
                ? {
                      onQuestionAnswer: (
                          callId: string,
                          payload: Parameters<
                              NonNullable<V3ChatActions['onQuestionAnswer']>
                          >[1],
                      ) =>
                          callbacksRef.current.onQuestionAnswer?.(
                              callId,
                              payload,
                          ),
                  }
                : {}),
            ...(hasRetry
                ? { onRetry: () => callbacksRef.current.onRetry?.() }
                : {}),
            ...(hasPromptSubmit
                ? {
                      onPromptSubmit: (prompt: string) => {
                          const callback = callbacksRef.current.onPromptSubmit;
                          return callback?.(prompt) ?? false;
                      },
                  }
                : {}),
        }),
        [
            actions.answeringQuestionCallIds,
            actions.decidingCallIds,
            actions.isRetryDisabled,
            hasQuestionAnswer,
            hasPromptSubmit,
            hasRetry,
            hasToolDecision,
        ],
    );
}
